/**
 * The speaker primitive's look: a floor-standing hi-fi speaker built from
 * primitives (cabinet, baffle, woofer, tweeter, bass port, status LED).
 * Drawn centred in its pick box; the front faces +Z. While it's playing
 * nearby the woofer breathes and the LED glows.
 */
import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useMusicStore } from '../../services/music/musicStore';

export const SPK_W = 0.46;
export const SPK_H = 0.95;
export const SPK_D = 0.4;
/** Centre height when standing on the floor. */
export const SPK_REST_Y = SPK_H / 2;

const noRay = () => null;

export const SpeakerModel3D: React.FC<{ id?: string; color?: string; selected?: boolean; editor?: boolean }> = ({ id, color = '#22272f', selected = false, editor = false }) => {
  const playing = useMusicStore((s) => !!id && s.speakers.some((sp) => sp.key === `speaker:${id}` && sp.playing));
  const woofer = useRef<THREE.Group>(null);
  const tweeter = useRef<THREE.Group>(null);
  const led = useRef<THREE.MeshBasicMaterial>(null);
  const mats = useMemo(() => ({
    baffle: new THREE.MeshStandardMaterial({ color: '#111418', roughness: 0.85 }),
    metal: new THREE.MeshStandardMaterial({ color: '#9aa7b4', metalness: 0.85, roughness: 0.3 }),
    cone: new THREE.MeshStandardMaterial({ color: '#3a414b', roughness: 0.7, side: THREE.DoubleSide }),
    cap: new THREE.MeshStandardMaterial({ color: '#2b3038', roughness: 0.35, metalness: 0.3 }),
    dome: new THREE.MeshStandardMaterial({ color: '#cfd6dd', metalness: 0.6, roughness: 0.25 }),
    plinth: new THREE.MeshStandardMaterial({ color: '#0c0e11', roughness: 0.7 }),
  }), []);
  React.useEffect(() => () => { Object.values(mats).forEach((m) => m.dispose()); }, [mats]);

  useFrame(({ clock }) => {
    const w = woofer.current;
    if (!w) return;
    if (playing) {
      // ~120 bpm kick; a soft pump, not a strobe.
      const t = clock.elapsedTime;
      const beat = Math.pow(Math.max(0, Math.sin(t * Math.PI * 4)), 6);
      w.scale.setScalar(1 + beat * 0.035);
      w.position.z = SPK_D / 2 + 0.004 + beat * 0.006;
      if (tweeter.current) tweeter.current.scale.setScalar(1 + Math.max(0, Math.sin(t * Math.PI * 16)) * 0.02);
    } else if (w.scale.x !== 1) {
      w.scale.setScalar(1); w.position.z = SPK_D / 2 + 0.004;
      tweeter.current?.scale.setScalar(1);
    }
    if (led.current) led.current.color.set(playing ? '#2ff5c8' : '#1d4a43');
  });

  const front = SPK_D / 2;
  return (
    <group>
      {/* cabinet */}
      <mesh castShadow receiveShadow raycast={editor ? noRay : undefined}>
        <boxGeometry args={[SPK_W, SPK_H - 0.04, SPK_D]} />
        <meshStandardMaterial color={color} roughness={0.55} metalness={0.05} emissive={selected ? '#0b3b36' : '#000000'} />
      </mesh>
      {/* plinth */}
      <mesh position={[0, -SPK_H / 2 + 0.02, 0]} material={mats.plinth} raycast={editor ? noRay : undefined}>
        <boxGeometry args={[SPK_W + 0.03, 0.04, SPK_D + 0.03]} />
      </mesh>
      {/* baffle */}
      <mesh position={[0, 0.0, front + 0.002]} material={mats.baffle} raycast={noRay}>
        <boxGeometry args={[SPK_W - 0.05, SPK_H - 0.1, 0.006]} />
      </mesh>
      {/* woofer */}
      <group ref={woofer} position={[0, -0.12, front + 0.004]}>
        <mesh material={mats.metal} raycast={noRay}><torusGeometry args={[0.155, 0.014, 12, 40]} /></mesh>
        <mesh material={mats.cone} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.02]} raycast={noRay}>
          <cylinderGeometry args={[0.145, 0.05, 0.05, 40, 1, true]} />
        </mesh>
        <mesh material={mats.cap} position={[0, 0, -0.03]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 0.55, 1]} raycast={noRay}><sphereGeometry args={[0.05, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} /></mesh>
      </group>
      {/* tweeter */}
      <group ref={tweeter} position={[0, 0.25, front + 0.004]}>
        <mesh material={mats.metal} raycast={noRay}><torusGeometry args={[0.058, 0.01, 10, 32]} /></mesh>
        <mesh material={mats.dome} rotation={[Math.PI / 2, 0, 0]} scale={[1, 0.6, 1]} raycast={noRay}><sphereGeometry args={[0.042, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} /></mesh>
      </group>
      {/* bass port */}
      <mesh material={mats.metal} position={[0, -0.36, front + 0.004]} raycast={noRay}><torusGeometry args={[0.032, 0.008, 8, 24]} /></mesh>
      <mesh position={[0, -0.36, front + 0.003]} raycast={noRay}><circleGeometry args={[0.03, 20]} /><meshBasicMaterial color="#050607" /></mesh>
      {/* status LED */}
      <mesh position={[SPK_W / 2 - 0.05, SPK_H / 2 - 0.08, front + 0.006]} raycast={noRay}>
        <circleGeometry args={[0.008, 12]} />
        <meshBasicMaterial ref={led} color="#1d4a43" toneMapped={false} />
      </mesh>
    </group>
  );
};

/** Viewer: the speaker standing where it was placed. */
export const ViewerSpeaker: React.FC<{ id: string; primitive: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number]; color: string } }> = ({ id, primitive }) => (
  <group position={primitive.position} rotation={primitive.rotation} scale={primitive.scale}>
    <SpeakerModel3D id={id} color={primitive.color} />
  </group>
);
